import React from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Create as CreateIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  Draw as SignIcon,
  CheckCircle as CompleteIcon,
  Cancel as DeclineIcon,
  Schedule as ExpireIcon,
  Block as RevokeIcon,
  Notifications as ReminderIcon,
  Download as DownloadIcon,
  Description as AuditIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { contractsApi } from '../../services/contractsApi';
import { formatDistanceToNow, format } from 'date-fns';

interface Props {
  contractId: string;
}

const ACTION_CONFIG: Record<
  string,
  { icon: React.ReactElement; color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'grey'; label: string }
> = {
  created: { icon: <CreateIcon />, color: 'primary', label: 'Contract Created' },
  sent: { icon: <SendIcon />, color: 'info', label: 'Sent for Signature' },
  viewed: { icon: <ViewIcon />, color: 'secondary', label: 'Viewed by Signer' },
  signed: { icon: <SignIcon />, color: 'success', label: 'Signed' },
  completed: { icon: <CompleteIcon />, color: 'success', label: 'Completed' },
  declined: { icon: <DeclineIcon />, color: 'error', label: 'Declined' },
  expired: { icon: <ExpireIcon />, color: 'warning', label: 'Expired' },
  revoked: { icon: <RevokeIcon />, color: 'error', label: 'Revoked' },
  reminder_sent: { icon: <ReminderIcon />, color: 'info', label: 'Reminder Sent' },
  downloaded: { icon: <DownloadIcon />, color: 'grey', label: 'Document Downloaded' },
  audit_downloaded: { icon: <AuditIcon />, color: 'grey', label: 'Audit Trail Downloaded' },
};

export const ContractActivityTimeline: React.FC<Props> = ({ contractId }) => {
  const {
    data: activities,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['contract-activities', contractId],
    queryFn: () => contractsApi.getActivities(contractId),
  });

  if (isLoading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ display: 'flex', mb: 2 }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load activity timeline. Please try again.
      </Alert>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No activity recorded yet.</Typography>
      </Box>
    );
  }

  return (
    <Timeline position="right" sx={{ p: 0 }}>
      {activities.map((activity, index) => {
        const config = ACTION_CONFIG[activity.action] || {
          icon: <CreateIcon />,
          color: 'grey' as const,
          label: activity.action,
        };

        return (
          <TimelineItem key={activity._id}>
            <TimelineOppositeContent
              sx={{ flex: 0.3, py: 1.5 }}
              color="text.secondary"
              variant="body2"
            >
              {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot color={config.color} variant="outlined">
                {config.icon}
              </TimelineDot>
              {index < activities.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1.5 }}>
              <Typography variant="subtitle2">{config.label}</Typography>
              {activity.metadata?.signerEmail && (
                <Typography variant="body2" color="text.secondary">
                  {activity.metadata.signerName || activity.metadata.signerEmail}
                </Typography>
              )}
              {activity.metadata?.declineReason && (
                <Typography variant="body2" color="error">
                  Reason: {activity.metadata.declineReason}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {format(new Date(activity.createdAt), 'MMM d, yyyy h:mm a')}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default ContractActivityTimeline;
